import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchableSelect from '../SearchableSelect';
import usuarioService, { Usuario } from '../../services/usuarioService';
import permisoUsuarioService from '../../services/permisoUsuarioService';
import { collectCatalogKeys, descendantsOfKey } from '../../config/permisosCatalogoHelper';
import { compactGrantedKeys, expandGrantedKeys, canPermission, isPageGranted } from '../../utils/permisoAuthz';
import { isSuperAdminRole } from '../../utils/sistemaAuthz';
import { isAdminRoleName } from '../../utils/ubicacionUsuarioRoles';
import { usePermissions } from '../../contexts/PermissionContext';
import { useSnackbar } from 'notistack';
import type { PermisoCatalogo, PermisoModulo, PermisoPagina } from '../../types/permisos';

interface PermisosUsuarioTabProps {
  username: string;
  currentUsername?: string;
  role?: string;
  granted: string[];
  onChange: (granted: string[]) => void;
  disabled?: boolean;
  locked?: boolean;
}

type PreviewState = {
  from: string;
  granted: string[];
};

const loginId = (u: Usuario): string => (u.usuario || u.username || '').trim();

const isGrantedInTree = (granted: string[], key: string, catalogo: PermisoCatalogo) => {
  const isControl = catalogo.modulos.some((mod) =>
    mod.paginas.some((page) => page.controles.some((control) => control.key === key)),
  );
  return isControl ? canPermission(granted, key, false) : isPageGranted(granted, key);
};

const triState = (
  granted: string[],
  key: string,
  descendants: string[],
  catalogo: PermisoCatalogo,
): { checked: boolean; indeterminate: boolean } => {
  const self = isGrantedInTree(granted, key, catalogo);
  if (descendants.length === 0) return { checked: self, indeterminate: false };
  const childOn = descendants.filter((d) => isGrantedInTree(granted, d, catalogo)).length;
  if (self && childOn === descendants.length) return { checked: true, indeterminate: false };
  if (childOn === 0 && !self) return { checked: false, indeterminate: false };
  if (childOn === descendants.length) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
};

const PermisosUsuarioTab: React.FC<PermisosUsuarioTabProps> = ({
  username,
  currentUsername,
  role,
  granted,
  onChange,
  disabled,
  locked,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { catalogo, ready } = usePermissions();
  const allKeys = useMemo(() => collectCatalogKeys(catalogo), [catalogo]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [copyFrom, setCopyFrom] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const previewSeq = useRef(0);
  const readOnly = Boolean(disabled || locked);
  const displayGranted = locked ? allKeys : preview?.granted ?? granted;
  const treeLocked = readOnly || loadingPreview;

  useEffect(() => {
    let cancelled = false;
    usuarioService
      .getUsuarios({ pageNumber: 1, pageSize: 500 })
      .then((list) => {
        if (!cancelled) setUsuarios(list);
      })
      .catch(() => {
        if (!cancelled) setUsuarios([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const commitGranted = (next: string[]) => {
    if (preview) {
      setPreview({ ...preview, granted: next });
      return;
    }
    onChange(next);
  };

  const applyToggle = (key: string, on: boolean) => {
    if (treeLocked) return;
    const descendants = descendantsOfKey(key, catalogo);
    const expanded = expandGrantedKeys(displayGranted, allKeys, catalogo);
    if (on) {
      expanded.add(key);
      descendants.forEach((d) => expanded.add(d));
    } else {
      expanded.delete(key);
      descendants.forEach((d) => expanded.delete(d));
    }
    commitGranted(compactGrantedKeys(expanded, catalogo));
  };

  const resolveGrantedDeUsuario = async (userId: string): Promise<PreviewState> => {
    const origen = usuarios.find((u) => loginId(u).toLowerCase() === userId.toLowerCase());
    const fromSuperAdmin = isSuperAdminRole([origen?.role ?? '']);
    const fromAdmin = isAdminRoleName(origen?.role) && !fromSuperAdmin;
    if (fromSuperAdmin || fromAdmin) {
      return { from: userId, granted: allKeys };
    }
    const matriz = await permisoUsuarioService.getMatriz(userId);
    return {
      from: userId,
      granted: compactGrantedKeys(expandGrantedKeys(matriz.granted, allKeys, catalogo), catalogo),
    };
  };

  const handleSelectCopyFrom = async (origen: string | null) => {
    const seq = previewSeq.current + 1;
    previewSeq.current = seq;
    setCopyFrom(origen);
    const userId = origen?.trim();
    if (!userId || locked) {
      setPreview(null);
      return;
    }
    try {
      setLoadingPreview(true);
      const next = await resolveGrantedDeUsuario(userId);
      if (previewSeq.current !== seq) return;
      setPreview(next);
    } catch {
      if (previewSeq.current !== seq) return;
      setPreview(null);
      enqueueSnackbar('No se pudieron leer los permisos de ese usuario', { variant: 'error' });
    } finally {
      if (previewSeq.current === seq) setLoadingPreview(false);
    }
  };

  const handleCopy = async () => {
    if (!preview) {
      enqueueSnackbar('Elegí un usuario para copiar sus permisos', { variant: 'warning' });
      return;
    }
    try {
      setApplying(true);
      onChange(preview.granted);
      setPreview(null);
      setCopyFrom(null);
      enqueueSnackbar(`Permisos copiados de ${preview.from}`, { variant: 'success' });
    } finally {
      setApplying(false);
    }
  };

  const copyOptions = useMemo(
    () =>
      usuarios
        .map((u) => {
          const id = loginId(u);
          const role = (u.role || '').trim();
          return { id, role };
        })
        .filter((row) => row.id && row.id.toLowerCase() !== username.trim().toLowerCase())
        .filter((row, idx, arr) => arr.findIndex((x) => x.id.toLowerCase() === row.id.toLowerCase()) === idx)
        .map((row) => ({
          value: row.id,
          label: row.role ? `${row.id} (${row.role})` : row.id,
        })),
    [usuarios, username],
  );

  const renderPagina = (page: PermisoPagina) => {
    const desc = descendantsOfKey(page.key, catalogo);
    const state = triState(displayGranted, page.key, desc, catalogo);
    return (
      <Box key={page.key} sx={{ pl: 2, py: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={state.checked}
              indeterminate={state.indeterminate}
              disabled={treeLocked}
              onChange={(_, checked) => applyToggle(page.key, checked)}
            />
          }
          label={<Typography variant="body2">{page.label}</Typography>}
        />
        {page.controles.length > 0 && (
          <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 0 }}>
            {page.controles.map((control) => (
              <FormControlLabel
                key={control.key}
                control={
                  <Checkbox
                    size="small"
                    checked={canPermission(displayGranted, control.key, false)}
                    disabled={treeLocked}
                    onChange={(_, checked) => applyToggle(control.key, checked)}
                  />
                }
                label={<Typography variant="caption">{control.label}</Typography>}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderModulo = (mod: PermisoModulo) => {
    const desc = descendantsOfKey(mod.key, catalogo);
    const state = triState(displayGranted, mod.key, desc, catalogo);
    return (
      <Accordion key={mod.key} disableGutters elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FormControlLabel
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            control={
              <Checkbox
                checked={state.checked}
                indeterminate={state.indeterminate}
                disabled={treeLocked}
                onChange={(_, checked) => applyToggle(mod.key, checked)}
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>{mod.label}</Typography>}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>{mod.paginas.map(renderPagina)}</AccordionDetails>
      </Accordion>
    );
  };

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {locked && (
        <Alert severity="info">
          {isSuperAdminRole([role ?? ''])
            ? 'SUPERADMIN tiene acceso a todas las funcionalidades. La matriz no se edita ni se guarda.'
            : 'Admin tiene acceso a todo el catálogo de este tenant. La matriz no se edita ni se guarda.'}
        </Alert>
      )}
      {!locked && (
        <>
          <Typography variant="body2" color="text.secondary">
            Marcá módulos, páginas y controles. Al copiar de un Admin u otro usuario, esa matriz es
            un punto de partida: podés quitar o agregar funcionalidades antes de pulsar Copiar.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <SearchableSelect
              label="Copiar de otro usuario"
              allowEmpty
              emptyOptionLabel="Elegir usuario"
              value={copyFrom}
              onChange={(v) => void handleSelectCopyFrom(v)}
              options={copyOptions}
              disabled={disabled || loadingPreview || applying}
              sx={{ minWidth: 260, flex: 1 }}
            />
            {(loadingPreview || applying) && <CircularProgress size={22} />}
            <Button
              variant="outlined"
              onClick={() => void handleCopy()}
              disabled={disabled || loadingPreview || applying || !preview}
            >
              Copiar
            </Button>
            <Button
              variant="outlined"
              onClick={() => commitGranted(allKeys)}
              disabled={disabled || loadingPreview || applying}
            >
              Otorgar todo
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => commitGranted([])}
              disabled={disabled || loadingPreview || applying}
            >
              Limpiar
            </Button>
          </Stack>
        </>
      )}

      {preview && (
        <Alert severity="warning">
          Vista previa de <strong>{preview.from}</strong>. Ajustá lo que quieras (sacar o agregar) y
          pulsá <strong>Copiar</strong> para asignarlos a este usuario.
        </Alert>
      )}

      {currentUsername && username.toLowerCase() === currentUsername.toLowerCase() && !locked && (
        <Typography variant="caption" color="text.secondary">
          Estás editando tu propio usuario. Los cambios se aplican al volver a ingresar o recargar.
        </Typography>
      )}

      <Box sx={{ opacity: preview ? 0.92 : 1 }}>{catalogo.modulos.map(renderModulo)}</Box>
    </Stack>
  );
};

export default PermisosUsuarioTab;
